import type { BedId } from "../audio/ambience-model.ts";

/** Prévias que tocam juntas, no máximo (o LRU de 3 do banco de leitos). */
const MAX_PREVIEWS = 3;

/**
 * As prévias de leitos da bancada (`?debug=sons`, F46): cada leito sozinho, no máximo 3 juntos (um
 * quarto para o mais antigo). Cada pedido leva uma ficha: o que chega depois de outro pedido do
 * mesmo leito (ou de um `stop`) é parado na hora, sem vazar a fonte nem o leito preso.
 */
export class BedPreviews {
  private readonly active = new Map<BedId, { token: object; stop?: () => void }>();
  private readonly order: BedId[] = [];
  private readonly start: (id: BedId, seam: boolean) => Promise<(() => void) | undefined>;
  private readonly onChange: (id: BedId, playing: boolean) => void;

  constructor(start: (id: BedId, seam: boolean) => Promise<(() => void) | undefined>, onChange: (id: BedId, playing: boolean) => void = () => undefined) {
    this.start = start;
    this.onChange = onChange;
  }

  /** Os leitos tocando (ou chegando), do mais antigo ao mais recente. */
  playing(): BedId[] {
    return [...this.order];
  }

  async play(id: BedId, seam: boolean): Promise<void> {
    this.stop(id);
    if (this.order.length >= MAX_PREVIEWS) this.stop(this.order[0]!);
    const token = {};
    this.active.set(id, { token });
    this.order.push(id);
    this.onChange(id, true);
    const stop = await this.start(id, seam);
    const current = this.active.get(id);
    // Outro pedido do mesmo leito (ou um `stop`) chegou antes: este para já.
    if (current?.token !== token) return stop?.();
    if (!stop) return this.stop(id);
    current.stop = stop;
  }

  stop(id: BedId): void {
    const current = this.active.get(id);
    if (!current) return;
    this.active.delete(id);
    this.order.splice(this.order.indexOf(id), 1);
    current.stop?.();
    this.onChange(id, false);
  }

  stopAll(): void {
    for (const id of [...this.order]) this.stop(id);
  }
}
